import {Component, Inject, LOCALE_ID} from '@angular/core';
import {Role} from '../../../model/auth/Role';
import {MobileAwareComponent} from '../../../common/mobile-aware/mobile-aware.component';
import {BreakpointObserver} from '@angular/cdk/layout';
import {Contest} from '../../../model/contest/Contest';
import {Region} from '../../../model/location/Region';
import {AuthService} from '../../../service/auth.service';
import {localizeUserExamGrade, UserExamGrade, UserExamStats} from '../../../model/stats/Stats';
import {NotificationHandler} from '../../../helper/notification.handler';
import {StatsService} from '../../../service/stats.service';
import {DEFAULT_PAGE, Page} from '../../../model/Page';
import {School, SchoolGrade, SchoolType} from '../../../model/location/School';
import {User} from '../../../model/auth/User';
import {LocationService} from '../../../service/location.service';
import {TenantService} from '../../../service/tenant.service';
import {ContestTaskType, localizeContestTaskType} from '../../../model/contest/ContestTask';
import {Locale} from '../../../model/Locale';

@Component({
  selector: 'app-exam-rating-stats',
  templateUrl: './exam-rating-stats.component.html',
  styleUrls: ['./exam-rating-stats.component.scss']
})
export class ExamRatingStatsComponent extends MobileAwareComponent {

  private readonly PAGE_SIZE = 20;
  readonly examGrades = UserExamGrade;
  readonly roles = [Role.Host, Role.Coordinator, Role.Teacher, Role.Student];

  readonly mobileColumnsToDisplay = ['grade', 'name', 'points'];
  readonly columnsToDisplay = ['grade', 'name', 'region', 'school_name', 'points'];

  competitionId?: string;

  user?: User | null = null;
  contest?: Contest;

  region?: Region;
  schoolGrade?: SchoolGrade;
  taskType?: ContestTaskType;
  schoolType?: SchoolType;
  school?: School;

  regions: Map<string, string> = new Map<string, string>();
  examStats: UserExamStats[] | null = null;

  inProgress = false;

  page: Page<never> = DEFAULT_PAGE;

  constructor(private statsService: StatsService,
              private locationService: LocationService,
              private authService: AuthService,
              private tenantService: TenantService,
              @Inject(LOCALE_ID) private locale: string,
              breakpointObserver: BreakpointObserver) {
    super(breakpointObserver);
  }

  async initContent() {
    const tenant = this.tenantService.getTenant();
    if (tenant && tenant.id) {
      this.competitionId = tenant.id;
    }

    this.loadRegions();

    const user = await this.authService.getUser();
    if (user) {
      if (user.region) {
        this.region = new Region(user.region, '');
      } else {
        this.region = new Region('-1', '');
      }
    }

    if (user && user.schoolGrade) this.schoolGrade = user.schoolGrade;

    if (user && user.schoolId) {
      this.loadSchool(user.schoolId, user);
    } else {
      this.school = undefined;
      this.user = user;
    }
  }

  localizeTaskType(taskType?: ContestTaskType) {
    return taskType ? localizeContestTaskType(taskType) : '';
  }

  private loadRegions(): void {
    this.locationService.getRegions(this.locale as Locale).subscribe(regions => {
      regions.forEach(region => this.regions.set(region.id, region.name));
    });
  }

  loadSchool(schoolId: string, user: User) {
    this.inProgress = true;

    this.locationService.getSchoolDetails(schoolId).subscribe(school => {
      this.school = school;
      this.schoolType = new SchoolType(school.type, '');

      this.user = user;
      this.inProgress = false;
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|School details fetch has failed message@@notifications-school-details-fetch-failed-error:School details fetch has failed`, error);
    });
  }

  loadUserExamStats(contest?: Contest, grade?: SchoolGrade, type?: ContestTaskType,
                    region?: Region, school?: School, reset = false) {
    if (!this.competitionId || !contest || !grade || !type) return;

    if (reset) {
      this.page = {
        max_items: this.PAGE_SIZE,
        asc: false
      };
      this.examStats = null;
    }

    this.inProgress = true;

    const schoolId = region ? school?.id : undefined;
    const regionId = region && region.id != '-1' ? region.id : undefined;
    this.statsService.getUserExamStats(this.competitionId, contest.id, grade, type, regionId, schoolId, this.page).subscribe(page => {
      const data = page.content || [];
      if (reset) {
        this.examStats = data;
      } else {
        if (!this.examStats) {
          this.examStats = data;
        } else {
          this.examStats = this.examStats.concat(data);
        }
      }

      this.inProgress = false;

      this.page.next_token = page.next_token;
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|Stats fetch has failed message@@notifications-stats-fetch-failed-error:Statistical data fetch has failed`, error);
    });
  }

  regionSelected(region: Region) {
    const regionChanged = this.region != region;
    this.region = region;

    if (regionChanged) this.loadUserExamStats(this.contest, this.schoolGrade, this.taskType, this.region, this.school, true);
  }

  schoolSelected(school?: School, region?: Region) {
    const schoolChanged = this.school != school;
    this.school = school;
    if (schoolChanged) this.region = region;

    if (this.region && schoolChanged) {
      this.loadUserExamStats(this.contest, this.schoolGrade, this.taskType, this.region, this.school, true);
    }
  }

  contestSelected(contest: Contest) {
    this.contest = contest;

    this.loadUserExamStats(this.contest, this.schoolGrade, this.taskType, this.region, this.school, true);
  }

  loadNextPage() {
    this.loadUserExamStats(this.contest, this.schoolGrade, this.taskType, this.region, this.school);
  }

  localizeExamGrade(grade: UserExamGrade) {
    return localizeUserExamGrade(grade);
  }

  gradeSelected(grade: SchoolGrade, type?: ContestTaskType) {
    this.schoolGrade = grade;
    this.taskType = type;

    this.loadUserExamStats(this.contest, this.schoolGrade, this.taskType, this.region, this.school, true);
  }
}
